import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { subMonths, format, subYears } from 'date-fns';

const AnalyticsContext = createContext();

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  const { user } = useAuth();
  const [analyticsData, setAnalyticsData] = useState({});
  const [predictiveData, setPredictiveData] = useState({});
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const apiUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Get authorization headers
  const getHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      'Content-Type': 'application/json',
    },
  });

  // Error handler
  const handleError = (error, context = '') => {
    console.error(`Analytics ${context} Error:`, error);
    const message = error.response?.data?.detail || error.message || 'An error occurred';
    setError(message);
    return null;
  };

  // Clear error
  const clearError = () => setError(null);

  // =============================================================================
  // DATA FETCHING
  // =============================================================================

  const fetchAnalyticsData = async (dateRange = null, filters = {}) => {
    try {
      setLoading(true);
      clearError();

      // Fetch data from all analytics endpoints
      const [goalAnalytics, okrAnalytics, performanceAnalytics] = await Promise.all([
        axios.get(`${apiUrl}/api/goals/analytics/summary`, getHeaders()),
        axios.get(`${apiUrl}/api/okr/analytics/summary`, getHeaders()),
        axios.get(`${apiUrl}/api/performance/analytics/summary`, getHeaders())
      ]);

      const data = {
        goals: goalAnalytics.data,
        okrs: okrAnalytics.data,
        performance: performanceAnalytics.data,
        lastUpdated: new Date()
      };

      setAnalyticsData(data);
      return data;
    } catch (error) {
      return handleError(error, 'fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // PREDICTIVE ANALYTICS
  // =============================================================================

  const generatePredictiveAnalytics = (data) => {
    try {
      const predictions = {
        goalCompletion: predictGoalCompletion(data.goals),
        okrProjections: predictOKRPerformance(data.okrs),
        performanceTrends: predictPerformanceTrends(data.performance),
        riskAssessment: assessPerformanceRisks(data),
        recommendations: generateRecommendations(data)
      };

      setPredictiveData(predictions);
      return predictions;
    } catch (error) {
      handleError(error, 'generate predictions');
      return {};
    }
  };

  const predictGoalCompletion = (goalData) => {
    // Simulate predictive analytics for goal completion
    const currentCompletionRate = goalData?.completion_rate || 0;
    const totalGoals = goalData?.total_goals || 0;
    
    // Generate historical trend data (simulated)
    const monthlyTrends = Array.from({ length: 12 }, (_, i) => {
      const month = subMonths(new Date(), 11 - i);
      const seasonalFactor = Math.sin((i * Math.PI) / 6) * 0.1 + 1; // Seasonal variation
      const trend = (currentCompletionRate / 100) * seasonalFactor + Math.random() * 0.1 - 0.05;
      
      return {
        month: format(month, 'MMM yyyy'),
        completion_rate: Math.max(0, Math.min(100, trend * 100)),
        goals_completed: Math.floor(totalGoals * trend * 0.8),
        prediction: i >= 9 // Last 3 months are predictions
      };
    });

    // Calculate projection for next quarter
    const recentTrend = monthlyTrends.slice(-3).reduce((sum, month) => sum + month.completion_rate, 0) / 3;
    const projectedCompletion = Math.min(100, recentTrend * 1.1); // 10% optimistic growth
    
    return {
      currentRate: currentCompletionRate,
      projectedRate: projectedCompletion,
      monthlyTrends,
      confidence: 0.85,
      factors: [
        'Historical performance trends',
        'Seasonal variations',
        'Current goal complexity'
      ]
    };
  };

  const predictOKRPerformance = (okrData) => {
    const currentScore = okrData?.avg_score || 0;
    const totalObjectives = okrData?.total_objectives || 0;
    
    // Generate quarterly projections
    const quarterlyProjections = Array.from({ length: 4 }, (_, i) => {
      const quarter = `Q${i + 1} 2024`;
      const trendFactor = 1 + (i * 0.05); // Assume 5% improvement per quarter
      const score = Math.min(1.0, currentScore * trendFactor);
      
      return {
        quarter,
        projected_score: score,
        confidence: Math.max(0.6, 0.9 - (i * 0.1)),
        objectives_at_risk: Math.floor(totalObjectives * (1 - score) * 0.3)
      };
    });

    return {
      currentScore,
      quarterlyProjections,
      recommendedActions: [
        'Focus on key results with low progress',
        'Increase check-in frequency',
        'Align objectives with strategic priorities'
      ]
    };
  };

  const predictPerformanceTrends = (performanceData) => {
    const avgScore = performanceData?.avg_overall_score || 0;
    const totalReviews = performanceData?.total_reviews || 0;
    
    // Generate performance trend predictions
    const trendAnalysis = {
      current_performance: avgScore,
      predicted_improvement: avgScore * 1.08, // 8% improvement prediction
      performance_distribution: {
        high_performers: Math.floor(totalReviews * 0.2),
        solid_performers: Math.floor(totalReviews * 0.6),
        needs_improvement: Math.floor(totalReviews * 0.2)
      },
      risk_factors: [
        'Workload distribution imbalance',
        'Skill gap in emerging technologies',
        'Limited feedback frequency'
      ]
    };

    return trendAnalysis;
  };

  const assessPerformanceRisks = (allData) => {
    const risks = [];

    // Goal completion risk
    if (allData.goals?.completion_rate < 70) {
      risks.push({
        type: 'goal_completion',
        severity: 'high',
        description: 'Goal completion rate below 70%',
        impact: 'Organizational objectives may not be met',
        recommendation: 'Review goal setting process and provide additional support'
      });
    }

    // OKR scoring risk
    if (allData.okrs?.avg_score < 0.6) {
      risks.push({
        type: 'okr_performance',
        severity: 'medium',
        description: 'OKR average score below 60%',
        impact: 'Strategic initiatives may fall behind',
        recommendation: 'Reassess OKR difficulty and provide coaching'
      });
    }

    // Performance review risk
    if (allData.performance?.completion_rate < 80) {
      risks.push({
        type: 'review_completion',
        severity: 'medium',
        description: 'Performance review completion below 80%',
        impact: 'Limited visibility into employee performance',
        recommendation: 'Send reminders and enforce review deadlines'
      });
    }

    return risks;
  };

  const generateRecommendations = (data) => {
    const recommendations = [];

    // Data-driven recommendations
    if (data.goals?.completion_rate > 90) {
      recommendations.push({
        type: 'goal_optimization',
        priority: 'low',
        title: 'Consider Setting Stretch Goals',
        description: 'High completion rate suggests goals may be too easy',
        action: 'Increase goal difficulty by 15-20% for next quarter'
      });
    }

    if (data.okrs?.avg_score < 0.7) {
      recommendations.push({
        type: 'okr_improvement',
        priority: 'high',
        title: 'Improve OKR Performance',
        description: 'OKR scores indicate need for better execution',
        action: 'Implement weekly OKR check-ins and coaching sessions'
      });
    }

    return recommendations;
  };

  // =============================================================================
  // REPORT GENERATION
  // =============================================================================

  const generateReport = async (reportConfig) => {
    try {
      setLoading(true);
      
      const report = {
        id: Date.now().toString(),
        ...reportConfig,
        data: await fetchReportData(reportConfig),
        generatedAt: new Date(),
        generatedBy: user?.id
      };

      setReports(prev => [report, ...prev]);
      return report;
    } catch (error) {
      handleError(error, 'generate report');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchReportData = async (config) => {
    const { dateRange, filters, metrics } = config;
    
    // Fetch data based on report configuration
    const data = await fetchAnalyticsData(dateRange, filters);
    
    // Process data based on selected metrics
    const processedData = {
      summary: generateReportSummary(data, metrics),
      charts: generateReportCharts(data, metrics),
      tables: generateReportTables(data, metrics),
      insights: generateReportInsights(data)
    };

    return processedData;
  };

  const generateReportSummary = (data, metrics) => {
    return {
      goals: {
        total: data.goals?.total_goals || 0,
        completed: data.goals?.completed_goals || 0,
        completion_rate: data.goals?.completion_rate || 0,
        avg_progress: data.goals?.avg_progress || 0
      },
      okrs: {
        total_objectives: data.okrs?.total_objectives || 0,
        avg_score: data.okrs?.avg_score || 0,
        objectives_on_track: Math.floor((data.okrs?.total_objectives || 0) * (data.okrs?.avg_score || 0))
      },
      performance: {
        total_reviews: data.performance?.total_reviews || 0,
        completed_reviews: data.performance?.completed_reviews || 0,
        completion_rate: data.performance?.completion_rate || 0,
        avg_score: data.performance?.avg_overall_score || 0
      }
    };
  };

  const generateReportCharts = (data, metrics) => {
    return {
      goalTrends: generateGoalTrendData(data.goals),
      okrDistribution: generateOKRDistributionData(data.okrs),
      performanceMetrics: generatePerformanceMetricData(data.performance)
    };
  };

  const generateReportTables = (data, metrics) => {
    return {
      topPerformers: generateTopPerformersData(data),
      underperformingAreas: generateUnderperformingAreasData(data),
      departmentComparison: generateDepartmentComparisonData(data)
    };
  };

  const generateReportInsights = (data) => {
    const insights = [];

    // Goal insights
    if (data.goals?.completion_rate > 80) {
      insights.push({
        type: 'positive',
        category: 'Goals',
        title: 'Strong Goal Performance',
        description: `Organization achieved ${data.goals.completion_rate}% goal completion rate`
      });
    }

    // OKR insights
    if (data.okrs?.avg_score > 0.8) {
      insights.push({
        type: 'positive',
        category: 'OKRs',
        title: 'Excellent OKR Execution',
        description: `Average OKR score of ${Math.round(data.okrs.avg_score * 100)}% indicates strong strategic alignment`
      });
    }

    return insights;
  };

  // Helper functions for chart data generation
  const generateGoalTrendData = (goalData) => {
    return Array.from({ length: 6 }, (_, i) => {
      const month = subMonths(new Date(), 5 - i);
      return {
        month: format(month, 'MMM'),
        completed: Math.floor(Math.random() * 20) + 10,
        total: Math.floor(Math.random() * 30) + 25
      };
    });
  };

  const generateOKRDistributionData = (okrData) => {
    return [
      { range: '0-20%', count: 2, color: '#EF4444' },
      { range: '21-40%', count: 5, color: '#F59E0B' },
      { range: '41-60%', count: 8, color: '#EAB308' },
      { range: '61-80%', count: 12, color: '#22C55E' },
      { range: '81-100%', count: 7, color: '#10B981' }
    ];
  };

  const generatePerformanceMetricData = (performanceData) => {
    return [
      { metric: 'Communication', score: 85 },
      { metric: 'Leadership', score: 78 },
      { metric: 'Technical Skills', score: 92 },
      { metric: 'Innovation', score: 88 },
      { metric: 'Teamwork', score: 90 }
    ];
  };

  const generateTopPerformersData = (data) => {
    return [
      { name: 'John Smith', department: 'Engineering', goal_completion: 95, okr_score: 0.9 },
      { name: 'Sarah Johnson', department: 'Marketing', goal_completion: 92, okr_score: 0.88 },
      { name: 'Mike Davis', department: 'Sales', goal_completion: 90, okr_score: 0.85 }
    ];
  };

  const generateUnderperformingAreasData = (data) => {
    return [
      { area: 'Customer Support Response Time', current: 65, target: 85, gap: -20 },
      { area: 'Product Development Velocity', current: 72, target: 90, gap: -18 },
      { area: 'Employee Training Completion', current: 68, target: 95, gap: -27 }
    ];
  };

  const generateDepartmentComparisonData = (data) => {
    return [
      { department: 'Engineering', goal_completion: 88, okr_score: 0.85, performance: 87 },
      { department: 'Marketing', goal_completion: 82, okr_score: 0.78, performance: 85 },
      { department: 'Sales', goal_completion: 91, okr_score: 0.82, performance: 89 },
      { department: 'HR', goal_completion: 76, okr_score: 0.73, performance: 83 }
    ];
  };

  // Initialize analytics data when user changes
  useEffect(() => {
    if (user) {
      fetchAnalyticsData();
    }
  }, [user]);

  // Generate predictions when analytics data changes
  useEffect(() => {
    if (analyticsData.goals) {
      generatePredictiveAnalytics(analyticsData);
    }
  }, [analyticsData]);

  const value = {
    // State
    analyticsData,
    predictiveData,
    reports,
    loading,
    error,

    // Actions
    fetchAnalyticsData,
    generatePredictiveAnalytics,
    generateReport,
    clearError,

    // Utility functions
    generateReportSummary,
    assessPerformanceRisks,
    generateRecommendations
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};